import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { qk } from '@/internationalization/query-keys'
import { tt } from '@/internationalization/i18n'
import { ApiError } from '@/internationalization/api-client'
import { toast } from '@/stores'
import type { UpcomingMeetings } from '@/types'
import { meetingsApi, type MeetingListParams } from './api'

/**
 * `placeholderData: keepPreviousData` is what makes paging and filtering feel
 * instant: the previous page stays on screen while the next one loads instead
 * of collapsing to a skeleton the user has already seen.
 */
export function useMeetings(params: MeetingListParams) {
  return useQuery({
    queryKey: qk.meetings.list(params),
    queryFn: () => meetingsApi.list(params),
    placeholderData: keepPreviousData,
  })
}

/**
 * Ask the recorder to join a pasted meeting link.
 *
 * THE LIST IS INVALIDATED because the row already exists by the time this
 * resolves: the API writes it before dispatching, so the meeting can be shown
 * as `scheduled` immediately and then move to `recording` on its own. Without
 * this the meeting a person just asked for is absent from the page they asked
 * from, which reads as the request having failed.
 *
 * THE SERVER'S REFUSAL IS SHOWN VERBATIM when it is a considered one (a 4xx).
 * "A recorder has already been asked to join this meeting" and "this workspace
 * is not set up for recording" are different problems with different actions,
 * and collapsing both into "could not start the recorder" hides which. A 5xx
 * stays generic: its message is for the log, not for the person.
 */
export function useRecordMeeting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: meetingsApi.record,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.meetings.all })
      toast.success(
        'Recorder request sent',
        'The bot is joining the Teams lobby. Ask the host to admit it.',
      )
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status < 500) {
        return toast.error('Could not start the recorder', error.message)
      }
      toast.error(
        'Could not start the recorder',
        'Something went wrong. Try again shortly.',
      )
    },
  })
}

/**
 * "Up next". Mostly calendar entries the database has never seen — a meeting
 * only acquires a row five minutes before it starts — so this is refetched on a
 * timer as well as on demand: the horizon moves, and a meeting that starts while
 * the tab is open should stop being "up next" without a reload.
 */
export function useUpcomingMeetings() {
  return useQuery({
    queryKey: qk.meetings.upcoming,
    queryFn: meetingsApi.upcoming,
    // Comfortably longer than the server's own calendar cache, so a poll is a
    // cheap read of that cache rather than a Graph request per tab.
    refetchInterval: 5 * 60_000,
  })
}

/**
 * The refresh button. Writes the fresh answer straight into the cache rather
 * than invalidating, because invalidating would trigger the plain GET — which
 * the server may serve from the cache the user just asked us to bypass.
 */
export function useRefreshUpcomingMeetings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: meetingsApi.refreshUpcoming,
    onSuccess: (data: UpcomingMeetings) => {
      client.setQueryData(qk.meetings.upcoming, data)
      if (!data.calendar.configured) {
        toast.warning(
          tt('meetings.toast.calendarNotConnectedTitle'),
          tt('meetings.toast.calendarNotConnectedBody'),
        )
      } else if (!data.calendar.ok) {
        toast.error(
          tt('meetings.toast.calendarUnreachableTitle'),
          tt('meetings.toast.calendarUnreachableBody'),
        )
      }
    },
    onError: () => toast.error(tt('meetings.toast.refreshFail')),
  })
}

export function useMeeting(id: string | undefined) {
  return useQuery({
    queryKey: qk.meetings.detail(id ?? ''),
    queryFn: () => meetingsApi.detail(id as string),
    enabled: Boolean(id),
    // A meeting still recording or processing changes on its own; a finished one
    // never does. Polling everything would be waste, polling nothing would
    // leave "Processing" on screen after the summary is ready.
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'recording' || status === 'processing' || status === 'joining' ? 15_000 : false
    },
  })
}

/**
 * A playable source for one meeting's recording.
 *
 * THE URL EXPIRES, which is what every option here is about. It is a
 * presigned S3 GET with a TTL the server states in the response, so this
 * treats it as perishable: stale well before it expires, refetched on focus,
 * and thrown away rather than cached across meetings.
 *
 * `retry: false` because the two failures that matter are both ANSWERS. A 404
 * means this meeting has no stored audio (never admitted, or not yet
 * transcribed) and a 503 means the deployment has no media bucket — retrying
 * either three times delays the honest empty state for no reason. A genuine
 * 5xx is the one case a retry would help, and it is rare enough not to buy
 * the other two.
 */
export function useMeetingAudio(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: qk.meetings.audio(id ?? ''),
    queryFn: () => meetingsApi.audio(id as string),
    enabled: Boolean(id) && enabled,
    retry: false,
    // Half the server's hour. Deriving it from `expiresInSeconds` on the
    // response would be exact and is not worth a second source of truth: the
    // failure mode of being early is one extra HEAD, and of being late is a
    // player that dies mid-sentence.
    staleTime: 25 * 60 * 1000,
    // Deliberately NOT longer than the client default. An entry here holds a
    // presigned URL -- a bearer credential for the recording -- so it should
    // not outlive the rest of the session's cache; sign-out empties the whole
    // client, and this must not be the one query that survives it longest.
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
}

/**
 * One meeting's screen-capture setting, and the write that changes it.
 *
 * THREE STATES, NOT TWO. `null` means INHERIT — clear the override and follow
 * the workspace. "Off" and "not set" are different answers: collapsing them
 * would mean an admin who later turns the workspace default ON could never
 * reach a meeting somebody had once turned off.
 *
 * Fetched lazily (`enabled`), because the endpoint is behind
 * `meeting:screen_capture` and the card it hangs off is visible to everybody.
 */
export function useMeetingScreenCapture(meetingId: string, enabled: boolean) {
  const client = useQueryClient()
  const query = useQuery({
    queryKey: qk.meetings.screenCapture(meetingId),
    queryFn: () => meetingsApi.screenCapture.get(meetingId),
    enabled,
  })
  const save = useMutation({
    mutationFn: (screenCapture: boolean | null) =>
      meetingsApi.screenCapture.set(meetingId, screenCapture),
    onSuccess: (next) => {
      client.setQueryData(qk.meetings.screenCapture(meetingId), next)
      toast.success(
        next.screenCapture ? 'Screen capture on for this meeting' : 'Screen capture off for this meeting',
        next.source === 'workspace' ? 'Following the workspace default.' : undefined,
      )
    },
    // The server refuses a meeting this person may not open (migration 024's
    // participation rule). That is a sentence worth showing rather than
    // flattening into "could not save" — it names a rule they can act on.
    onError: (error: unknown) =>
      toast.error(
        'Could not change screen capture',
        error instanceof Error ? error.message : undefined,
      ),
  })
  return { query, save }
}
